/**
 * 向导通用小工具：Step1 字段缓存 key、改表判断、YAML 工作流拷贝等。
 * 从 `useModelAgentCreateWizard` 拆出，避免主 hook 膨胀。
 */
import { INITIAL_YAML_WORKFLOW } from '../constant';
import type {
  IStepFieldConfigState,
  IStepSelectTablesState,
  IYamlWorkflow,
  TStepYamlDraftPatch,
} from '../types';

/**
 * Step1 选表快照 key（game + 库 + schema + 表 id 列表）。
 * 与 `fieldConfigLoadKeyRef` 对比，用于 get_field_config 是否可跳过。
 */
export const buildFieldConfigLoadKey = (
  gameCode: string,
  selectTables: Pick<IStepSelectTablesState, 'databaseValue' | 'schemaValue' | 'tablesValue'>,
): string =>
  JSON.stringify({
    gameCode,
    databaseValue: selectTables.databaseValue,
    schemaValue: selectTables.schemaValue,
    tablesValue: selectTables.tablesValue,
  });

/** 是否相对上次成功拉字段时更换了库/表（首次进入时 previous 为空，不算改表） */
export const isTableSelectionChanged = (
  loadKey: string,
  previousLoadKey: string,
): boolean => Boolean(previousLoadKey) && loadKey !== previousLoadKey;

/** Step1 选表未变、已有列且无拉取错误时可不进接口直接进 Step2 */
export const isFieldConfigCacheHit = (
  loadKey: string,
  previousLoadKey: string,
  fieldConfig: Pick<IStepFieldConfigState, 'fieldColumns' | 'fieldError'>,
): boolean =>
  loadKey === previousLoadKey &&
  fieldConfig.fieldColumns.length > 0 &&
  !fieldConfig.fieldError;

/**
 * 改表后 YAML 草稿重置片段；不碰 savedConfigUuid，编辑态仍可对同 uuid save。
 */
export const yamlDraftResetForNewTable = (
  tablesName: string,
): TStepYamlDraftPatch => ({
  cube_name: tablesName.trim(),
  yamlReady: false,
  yamlText: '',
  yamlLoading: false,
  yamlError: false,
});

/** 深拷贝 Step4 工作流初始态，避免 rows 与 `INITIAL_YAML_WORKFLOW` 共享引用 */
export const cloneYamlWorkflowState = (): IYamlWorkflow => ({
  ...INITIAL_YAML_WORKFLOW,
  rows: INITIAL_YAML_WORKFLOW.rows.map((r) => ({ ...r })),
});

/** Step2 拉字段 loading/error 的 patch 片段，便于 setFieldConfig 合并 */
export const setFieldConfigLoading = (
  loading: boolean,
  error = false,
): Pick<IStepFieldConfigState, 'fieldLoading' | 'fieldError'> => ({
  fieldLoading: loading,
  fieldError: error,
});
